// frontend/src/App.js
import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { CssBaseline } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import LoginPage from './pages/auth/LoginPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import VerifyCompanyPage from './pages/auth/VerifyCompanyPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import RegisterPage from './pages/auth/RegisterPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import ProfilePage from './pages/dashboard/ProfilePage';
import ClientPage from './pages/dashboard/ClientPage';
import ProjectPage from './pages/dashboard/ProjectPage';
import TaskPage from './pages/dashboard/TaskPage';
import PackagePage from './pages/dashboard/PackagePage';
import TeamPage from './pages/dashboard/TeamPage';
import CalendarPage from './pages/dashboard/CalendarPage';
import DashboardLayout from './components/layout/DashboardLayout';
import ProjectBoardPage from './pages/dashboard/ProjectBoardPage';
import StatusPage from './pages/dashboard/StatusPage';
import PermissionsPage from './pages/dashboard/PermissionsPage';
import BillingPage from './pages/dashboard/BillingPage';
import KpiPerformancePage from './pages/dashboard/KpiPerformancePage';
import MasterLoginPage from './pages/master/LoginPage';
import MasterDashboard from './pages/master/DashboardPage';
import SprintPage from './pages/dashboard/SprintPage';
import BacklogPage from './pages/dashboard/BacklogPage';
import TeamDetailsPage from './pages/dashboard/TeamDetailsPage';
import { pollingManager } from './services/PollingManager';
import DocumentationPage from "./pages/dashboard/Documentation";

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
  },
});

// ADD THIS COMPONENT - Configure polling intervals
const PollingConfig = () => {
  useEffect(() => {
    // Configure page-specific polling intervals
    pollingManager.configurePageIntervals({
      projects: 15000,      // 15 seconds
      tasks: 10000,         // 10 seconds
      comments: 8000,       // 8 seconds (faster for active discussions)
      notifications: 12000, // 12 seconds
      calendar: 30000,      // 30 seconds (less frequent)
      sprints: 20000,       // 20 seconds
      backlog: 15000,       // 15 seconds
      'kpi-approvals': 30000, // 30 seconds
    });

    // Cleanup on unmount
    return () => {
      pollingManager.stopAll();
    };
  }, []);

  return null;
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated) return children;

  const roleName = user?.teamMembership?.role?.name
    ?.toLowerCase()
    ?.trim();

  const isClient = !!user?.client_id;

  // ✅ Client
  if (isClient) {
    return <Navigate to="/projects" />;
  }

  // ✅ System Administrator
  if (roleName === "system administrator") {
    return <Navigate to="/dashboard" />;
  }

  // ✅ Other users
  return <Navigate to="/board" />;
};

const AdminProtectedRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) return <div>Loading...</div>;

  const isAdmin = user?.teamMembership?.role?.name?.toLowerCase() === "system administrator";

  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!isAdmin) return <Navigate to="/dashboard" />;

  return children;
};

const MasterProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('master_token');
  if (!token) return <Navigate to="/master/login" />;
  return children;
};

const PermissionProtectedRoute = ({ children, permission }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) return <div>Loading...</div>;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!user?.hasPermission(permission)) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

function App() {
  const { user } = useAuth();
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <PollingConfig />
      <Routes>
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/verify-email/:id/:hash" element={<PublicRoute><VerifyEmailPage /></PublicRoute>} />
        <Route path="/verify-company/:token" element={<PublicRoute><VerifyCompanyPage /></PublicRoute>} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
        <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />

        <Route path="/master/login" element={<MasterLoginPage />} />
        <Route path="/master/dashboard" element={
          <MasterProtectedRoute>
            <MasterDashboard />
          </MasterProtectedRoute>
        } />

        {/* Dashboard: Full-width */}
        <Route path="/dashboard" element={
          <PermissionProtectedRoute permission="dashboard.view.page">
            <DashboardLayout noSidebarMargin={true}>
              <DashboardPage />
            </DashboardLayout>
          </PermissionProtectedRoute>
        } />

        {/* Profile: Full-width */}
        <Route path="/profile" element={
          <ProtectedRoute>
            <DashboardLayout noSidebarMargin={true}><ProfilePage /></DashboardLayout>
          </ProtectedRoute>
        } />

        <Route path="/kpi-approvals" element={
          <PermissionProtectedRoute permission="team.update">
            <DashboardLayout><KpiPerformancePage /></DashboardLayout>
          </PermissionProtectedRoute>
        } />

        {/* All other pages: With sidebar margin */}
        <Route path="/statuses" element={
          <PermissionProtectedRoute permission="status.view.page">
            <DashboardLayout noSidebarMargin={true}><StatusPage /></DashboardLayout>
          </PermissionProtectedRoute>
        } />
        <Route path="/clients" element={
          <PermissionProtectedRoute permission="client.view.page">
            <DashboardLayout noSidebarMargin={true}><ClientPage /></DashboardLayout>
          </PermissionProtectedRoute>
        } />
        <Route path="/projects" element={
          <ProtectedRoute>
            <DashboardLayout noSidebarMargin={true}><ProjectPage /></DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/projects/:projectId/sprints" element={
          <PermissionProtectedRoute permission="project.board.view">
            <DashboardLayout noSidebarMargin={true}><SprintPage /></DashboardLayout>
          </PermissionProtectedRoute>
        } />
        <Route path="/board" element={
          <PermissionProtectedRoute permission="project.board.view">
            <DashboardLayout noSidebarMargin={true}><ProjectBoardPage /></DashboardLayout>
          </PermissionProtectedRoute>
        } />
        <Route path="/projects/:id/board" element={
          <PermissionProtectedRoute permission="project.board.view">
            <DashboardLayout noSidebarMargin={true}><ProjectBoardPage /></DashboardLayout>
          </PermissionProtectedRoute>
        } />
        <Route path="/projects/:projectId/backlog" element={
          <PermissionProtectedRoute permission="backlog.view">
            <DashboardLayout><BacklogPage /></DashboardLayout>
          </PermissionProtectedRoute>
        } />
        <Route path="/tasks" element={
          <PermissionProtectedRoute permission="task.view.page">
            <DashboardLayout noSidebarMargin={true}><TaskPage /></DashboardLayout>
          </PermissionProtectedRoute>
        } />
        <Route path="/packages" element={
          <AdminProtectedRoute>
            <DashboardLayout noSidebarMargin={true}><PackagePage /></DashboardLayout>
          </AdminProtectedRoute>
        } />
        <Route path="/team" element={
          <PermissionProtectedRoute permission="team.view.page">
            <DashboardLayout noSidebarMargin={true}><TeamPage /></DashboardLayout>
          </PermissionProtectedRoute>
        } />
        <Route path="/team-details" element={
          <PermissionProtectedRoute permission="team.view.page">
            <DashboardLayout><TeamDetailsPage /></DashboardLayout>
          </PermissionProtectedRoute>
        } />
        <Route path="/permissions" element={
          <ProtectedRoute>
            <DashboardLayout noSidebarMargin={true}><PermissionsPage /></DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/billing" element={
          <PermissionProtectedRoute permission="billing.manage">
            <DashboardLayout noSidebarMargin={true}><BillingPage /></DashboardLayout>
          </PermissionProtectedRoute>
        } />
        <Route path="/calendar" element={
          <ProtectedRoute>
            <DashboardLayout noSidebarMargin={true}><CalendarPage /></DashboardLayout>
          </ProtectedRoute>
        } />

        <Route path="/documentation" element={
          <ProtectedRoute>
            <DashboardLayout noSidebarMargin={true}><DocumentationPage /></DashboardLayout>
          </ProtectedRoute>
        } />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              {user?.client_id ? (
                <Navigate to="/projects" />
              ) : user?.teamMembership?.role?.name
                ?.toLowerCase()
                ?.trim() === "system administrator" ? (
                <Navigate to="/dashboard" />
              ) : (
                <Navigate to="/board" />
              )}
            </ProtectedRoute>
          }
        />
      </Routes>
    </ThemeProvider>
  );
}

export default App;